<template>
  <div class="cf-page-container">
    <div
      v-if="currentUser.isRefreshing"
      :class="['cf-container-full-width', { 'cf-page-loader': currentUser.isRefreshing }]"
    >
      <div class="cf-loader" />
    </div>

    <template v-else>
      <div class="cf-card cf-card--mobile cf-flex cf-flex--column cf-flex--gap-16">
        <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between">
          <h1 class="cf-title cf-title--1">
            My Account
          </h1>

          <button
            type="button"
            class="cf-button cf-button--primary cf-button--sm"
            @click="signOut"
          >
            Sign Out
          </button>
        </div>

        <cf-verify-account-cta />

        <!-- Account Info -->
        <cf-validated-form
          name="profile"
          @valid-submit="updateProfile"
        >
          <div class="cf-flex cf-flex--column cf-flex--gap-16">
            <div class="cf-flex cf-flex--column cf-flex--gap-10">
              <cf-full-name-input v-model="fullName" />

              <cf-text-input
                v-if="merchant.userSignUpFields.company || company"
                v-model="company"
                type="text"
                label="Company Name"
                name="company"
                icon-class="fa-building"
                autocomplete="organization"
                :show-as-optional="true"
                :validations="{
                  required: merchant.userSignUpFields.company && merchant.userSignUpFields.company.isRequired
                }"
              />

              <div class="cf-grid cf-grid--2-by-2-grid-column-row">
                <cf-text-input
                  v-if="merchant.userSignUpFields.email || email"
                  v-model="email"
                  type="email"
                  label="Email"
                  name="email"
                  autocomplete="emal"
                  :show-as-optional="true"
                  :validations="{
                    required: merchant.userSignUpFields.email && merchant.userSignUpFields.email.isRequired
                  }"
                />

                <cf-text-input
                  v-if="merchant.userSignUpFields.primarySmsNumber || primarySmsNumber"
                  v-model="primarySmsNumber"
                  type="phone"
                  label="Phone Number"
                  name="primarySmsNumber"
                  autocomplete="tel"
                  :show-as-optional="true"
                  :validations="{
                    required: merchant.userSignUpFields.primarySmsNumber && merchant.userSignUpFields.primarySmsNumber.isRequired
                  }"
                />

                <cf-text-input
                  v-if="merchant.userSignUpFields.zip || zip"
                  v-model="zip"
                  type="text"
                  label="Postal Code"
                  name="zip"
                  mask="postalCode"
                  icon-class="fa-map-marker-alt"
                  inputmode="numeric"
                  autocomplete="postal-code"
                  :show-as-optional="true"
                  :validations="{
                    required: merchant.userSignUpFields.zip && merchant.userSignUpFields.zip.isRequired,
                    postal_code: true
                  }"
                />

                <cf-birthday-input
                  v-if="merchant.userSignUpFields.birthdayInfo || dateOfBirth"
                  v-model="dateOfBirth"
                  :disabled="currentUser.dateOfBirth && loyalty.isEnrolled && (!merchant.userSignUpFields['birthdayInfo.year'] || currentUser.dateOfBirth.length === 10)"
                  :include-year="!!merchant.userSignUpFields['birthdayInfo.year']"
                  :required="!!merchant.userSignUpFields.birthdayInfo && merchant.userSignUpFields.birthdayInfo.isRequired"
                />
              </div>
            </div>

            <button
              type="submit"
              class="cf-button cf-button--primary cf-button--md"
              :class="{ 'cf-button--loading': isUpdatingProfile }"
            >
              Save
            </button>
          </div>
        </cf-validated-form>
      </div>

      <!-- Password Info -->
      <div
        v-if="!currentUser.usesThirdPartyAuthentication"
        class="cf-card cf-card--mobile cf-flex cf-flex--column"
      >
        <cf-validated-form
          name="password"
          @valid-submit="updatePassword"
        >
          <div class="cf-flex cf-flex--column cf-flex--gap-16">
            <div class="cf-flex cf-flex--column cf-flex--gap-12">
              <h2 class="cf-title cf-title--2">
                Password
              </h2>

              <h4 class="cf-title cf-title--4">
                1. Please enter your current password:
              </h4>

              <cf-text-input
                v-model="oldPassword"
                type="password"
                label="Current Password"
                name="oldPassword"
                :validations="{
                  required: true
                }"
              />
            </div>

            <div class="cf-flex cf-flex--column cf-flex--gap-12">
              <h4 class="cf-title cf-title--4">
                2. Enter a new password:
              </h4>

              <cf-text-input
                v-model="newPassword"
                type="password"
                label="New Password"
                name="newPassword"
                :validations="{
                  required: true,
                  strong_password: true
                }"
              />
            </div>

            <button
              type="submit"
              class="cf-button cf-button--primary cf-button--md"
              :class="{ 'cfbutton--loading': isUpdatingPassword }"
            >
              Update Password
            </button>
          </div>
        </cf-validated-form>
      </div>

      <!-- Notification Preferences -->
      <div class="cf-card cf-card--mobile cf-flex cf-flex--column cf-flex--gap-16">
        <cf-validated-form
          name="notifications"
          @valid-submit="updateNotifications"
        >
          <div class="cf-flex cf-flex--column cf-flex--gap-16">
            <div class="cf-flex cf-flex--column cf-flex--gap-12">
              <h2 class="cf-title cf-title--2">
                Communication Preferences
              </h2>

              <p class="cf-subtitle cf-subtitle--2">
                Choose what types of communications you’d like to receive from us and how you’d like to receive them.
              </p>
            </div>

            <div class="cf-flex cf-flex--column cf-flex--gap-12">
              <h4 class="cf-title cf-title--4">
                Categories
              </h4>

              <cf-switch-input
                v-model="notificationPreferences.marketingOptIn"
                label="Updates, news, and marketing"
                name="marketingNotifications"
              />
            </div>

            <template v-if="notificationPreferences.marketingOptIn">
              <cf-title-separator :omit-title="true" />

              <div class="cf-flex cf-flex--column cf-flex--gap-12">
                <h4 class="cf-title cf-title--4">
                  Notification Preferences
                </h4>

                <cf-switch-input
                  v-model="notificationPreferences.email"
                  label="Email"
                  name="emailNotifications"
                />

                <cf-switch-input
                  v-model="notificationPreferences.sms"
                  :show-info-icon="!showSmsDisclaimer"
                  :description="smsTermsContent"
                  :disabled="smsTogglerDisabled"
                  label="SMS"
                  name="smsNotifications"
                />

                <p
                  v-if="showSmsDisclaimer"
                  class="cf-subtitle cf-subtitle--2 cf-content"
                  v-html="smsTermsContent"
                />
              </div>
            </template>

            <button
              type="submit"
              class="cf-button cf-button--primary cf-button--md"
              :class="{ 'cf-button--loading': isUpdatingNotifications }"
            >
              Save
            </button>
          </div>
        </cf-validated-form>
      </div>

      <!-- Request Account Deletion -->
      <div class="cf-card cf-card--mobile cf-flex cf-flex--column cf-flex--align-start cf-flex--gap-16">
        <div class="cf-flex cf-flex--column cf-flex--gap-12">
          <h2 class="cf-title cf-title--2">
            Request Account Deletion
          </h2>

          <p class="cf-subtitle cf-subtitle--2">
            Deleting your account is a permanent action that will delete your account.
          </p>
        </div>

        <button
          class="cf-link cf-link--red"
          type="button"
          @click="$openModal('deleteMyAccount')"
        >
          Delete My Account
        </button>
      </div>
    </template>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';
  import http from '@/services/http';

  import parseTwoFactorError from '@/helpers/parse-two-factor-error';

  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';
  import CfFullNameInput from '@/view/components/shared/cf-full-name-input/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';
  import CfSwitchInput from '@/view/components/shared/cf-switch-input/index.vue';
  import CfTitleSeparator from '@/view/components/shared/cf-title-separator/index.vue';
  import CfBirthdayInput from '@/view/components/shared/cf-birthday-input/index.vue';

  import CfVerifyAccountCta from '@/view/components/globals/cf-verify-account-cta/index.vue';


  export default {
    name: 'OrderMyAccountPage',

    components: {
      CfValidatedForm,
      CfFullNameInput,
      CfSwitchInput,
      CfTextInput,
      CfTitleSeparator,
      CfBirthdayInput,
      CfVerifyAccountCta
    },

    metaInfo() {
      return {
        title: 'My Account'
      };
    },

    data() {
      return {
        testValue: null,
        company: null,
        dateOfBirth: null,
        email: null,
        fullName: null,
        isUpdatingNotifications: false,
        isUpdatingPassword: false,
        isUpdatingProfile: false,
        notificationPreferences: {
          email: false,
          marketingOptIn: false,
          sms: false
        },
        newPassword: null,
        oldPassword: null,
        primarySmsNumber: null,
        zip: null
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('session', ['currentUser', 'session']),
      ...mapGetters('offers', ['loyalty']),

      showSmsDisclaimer() {
        return this.merchant.options.showSmsDisclaimer;
      },

      smsTermsContent() {
        return this.content.MyAccount.metadata.smsTerms;
      },

      smsTogglerDisabled() {
        return !this.merchant.options.smsPreferenceToggleEnabled;
      }
    },

    created() {
      this.createdHandler();
    },

    methods: {
      async createdHandler() {
        if (!this.session.initialPageLoad) {
          await this.$store.dispatch('session/refreshProfile');
        }

        this.company = this.currentUser.company;
        this.dateOfBirth = this.currentUser.dateOfBirth;
        this.email = this.currentUser.email;
        this.notificationPreferences = {
          ...this.currentUser.notificationPreferences
        };
        this.fullName = this.currentUser.fullName;
        this.primarySmsNumber = this.currentUser.primarySmsNumber;
        this.zip = this.currentUser.zip;
      },

      signOut() {
        this.$store.dispatch('session/signOut', {
          userInitiated: true
        });

        this.$router.push({
          name: 'order',
          params: {
            merchantId: this.$route.params.merchantId
          }
        });

        // this.$triggerGoogleTrackingEvent('SignOut', 'Account');

        this.$triggerNewRelicEvent('SignOut', 'Information', 'Signed Out');
      },

      async updateNotifications() {
        try {
          this.isUpdatingNotifications = true;

          const preferences = this.notificationPreferences.marketingOptIn ? this.notificationPreferences : {
            email: false,
            marketingOptIn: false,
            sms: false
          };

          // this.$triggerGoogleTrackingEvent('MarketingOptIn', 'Account');

          this.$triggerNewRelicEvent('MarketingOptIn', 'Information', 'Account Marketing Opt In');

          await this.$store.dispatch('session/updateNotifications', preferences);

          this.notificationPreferences = {
            ...this.currentUser.notificationPreferences
          };

          events.emit('notification', {
            type: 'success',
            message: 'Your notification preferences were updated.'
          });
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.isUpdatingNotifications = false;
        }
      },

      async updatePassword() {
        try {
          this.isUpdatingPassword = true;

          await http.put('/cardfree-api/v1/account/me/password', {
            newPassword: this.newPassword,
            oldPassword: this.oldPassword
          });

          // this.$triggerGoogleTrackingEvent('UpdatePassword', 'Account');

          this.$triggerNewRelicEvent('UpdatePassword', 'Information', 'Updated Password');

          events.emit('notification', {
            type: 'success',
            message: 'Your password has been updated!'
          });
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.isUpdatingPassword = false;
        }
      },

      async updateProfile() {
        this.isUpdatingProfile = true;
        const emailWasChanged = this.currentUser.email !== this.email;

        try {
          await this.$store.dispatch('session/updateProfile', {
            company: this.company,
            dateOfBirth: this.dateOfBirth,
            email: this.email,
            fullName: this.fullName,
            primarySmsNumber: this.primarySmsNumber,
            zip: this.zip
          });

          events.emit('notification', {
            type: 'success',
            message: 'Your profile was updated.'
          });

          if (emailWasChanged) {
            this.$openModal('notice', {
              description: 'Your email change request was submitted. A verification email has been sent to your updated email address.'
            });
          }
        }

        catch (error) {
          this.updateAccountError(error);
        }

        finally {
          this.isUpdatingProfile = false;
        }
      },

      updateAccountError(error) {
        const { requiresAuth, ...rest } = parseTwoFactorError(error, '', this.primarySmsNumber);

        if (requiresAuth) {
          const twoFactorModalPayload = { ...rest };

          this.$openModal('twoFactorAuthModal', twoFactorModalPayload);
        }
        else {
          errorHandlers.generic(error);
        }
      }
    }
  };
</script>
